<template>
    <div class="rounded-sm overflow-auto bg-gray-50 flex flex-col gap-2">
        <div class="bg-gray-100 flex flex-col p-3 pb-2 gap-2">
            <div class="flex flex-row justify-between items-center gap-2">
                <h2 class="text-2xl space-x-1">
                    <span class="font-semibold text-gray-700">
                        {{
                            $t('count-allocation-variants', {
                                countAllocationVariants,
                                allocationGroupType: props.type,
                            })
                        }}
                    </span>
                    <span class="font-light text-gray-500">{{ $t('within-timeline-range') }}</span>
                </h2>

                <Button
                    class="text-gray-700"
                    size="Small"
                    variant="Transparent-Light"
                    :icon-description="$t('close-allocation-variant-selection-panel')"
                    @click="
                        triggerHideTooltip();
                        emit('close');
                    "
                >
                    <template #icon>
                        <CloseIcon class="size-4.5" />
                    </template>
                </Button>
            </div>

            <div class="flex flex-row justify-end text-xs items-center gap-0.5 font-light flex-wrap">
                <i18n v-if="type === 'Event'" path="toggle-visibility-explanation-event">
                    <template #activeBadge>
                        <Badge variant="Green" size="Extra-Small">
                            {{ $t('active-variants-badge-label') }}
                        </Badge>
                    </template>
                    <template #passiveBadge>
                        <Badge variant="Yellow" size="Extra-Small">
                            {{ $t('passive-variants-badge-label') }}
                        </Badge>
                    </template>
                    <template #completedBadge>
                        <Badge variant="Gray" size="Extra-Small">
                            {{ $t('completed-variants-badge-label') }}
                        </Badge>
                    </template>
                    <template #canceledBadge>
                        <Badge variant="Purple" size="Extra-Small">
                            {{ $t('canceled-variants-badge-label') }}
                        </Badge>
                    </template>
                </i18n>
                <i18n v-if="type === 'Project'" path="toggle-visibility-explanation-project">
                    <template #activeBadge>
                        <Badge variant="Green" size="Extra-Small">
                            {{ $t('active-variants-badge-label') }}
                        </Badge>
                    </template>
                    <template #completedBadge>
                        <Badge variant="Gray" size="Extra-Small">
                            {{ $t('completed-variants-badge-label') }}
                        </Badge>
                    </template>
                </i18n>
                <i18n v-if="type === 'ExternalEvent'" path="toggle-visibility-explanation-external-event">
                    <template #fixBadge>
                        <Badge variant="Green" size="Extra-Small">
                            {{ $t('fix-variants-badge-label') }}
                        </Badge>
                    </template>
                    <template #optionalBadge>
                        <Badge variant="Yellow" size="Extra-Small">
                            {{ $t('optional-variants-badge-label') }}
                        </Badge>
                    </template>
                    <template #completedBadge>
                        <Badge variant="Gray" size="Extra-Small">
                            {{ $t('completed-variants-badge-label') }}
                        </Badge>
                    </template>
                </i18n>

                <div class="flex flex-row gap-1 ml-0.5">
                    <Tooltip :is-teleported="true">
                        <template #trigger>
                            <Button
                                size="Extra-Small"
                                variant="Neutral-Light"
                                @click="
                                    triggerHideTooltip();
                                    emit('make-main-variants-visible');
                                "
                            >
                                <template #icon>
                                    <ToggleOnIcon class="size-4" />
                                </template>
                                {{ $t('make-main-variants-visible-label') }}
                            </Button>
                        </template>

                        <template #text>
                            <i18n
                                v-if="type === 'Event'"
                                path="make-main-variants-visible-tooltip-event"
                                :args="{ allocationGroupType: props.type }"
                            >
                                <template #activeBadge>
                                    <Badge size="Extra-Small" variant="Green">
                                        {{ $t('active-variants-badge-label') }}
                                    </Badge>
                                </template>
                                <template #passiveBadge>
                                    <Badge variant="Yellow" size="Extra-Small">
                                        {{ $t('passive-variants-badge-label') }}
                                    </Badge>
                                </template>
                                <template #completedBadge>
                                    <Badge size="Extra-Small" variant="Gray">
                                        {{ $t('completed-variants-badge-label') }}
                                    </Badge>
                                </template>
                                <template #canceledBadge>
                                    <Badge variant="Purple" size="Extra-Small">
                                        {{ $t('canceled-variants-badge-label') }}
                                    </Badge>
                                </template>

                                <template #mainVariants>
                                    <span class="font-semibold">
                                        {{ $t('main-variants') }}
                                    </span>
                                </template>

                                <template #mainVariantIndicator>
                                    <span class="relative inline-flex w-6">
                                        <span
                                            class="absolute -bottom-1.75 rounded inline-flex items-center justify-center text-white size-6 bg-sky-700"
                                        >
                                            <StarFilledIcon class="size-4" />
                                        </span>
                                    </span>
                                </template>

                                <template #toggleActionLabel>
                                    <span class="font-semibold">
                                        {{ $t('visible-toggle-label') }}
                                    </span>
                                </template>
                            </i18n>
                            <i18n
                                v-if="type === 'Project'"
                                path="make-main-variants-visible-tooltip-project"
                                :args="{ allocationGroupType: props.type }"
                            >
                                <template #activeBadge>
                                    <Badge size="Extra-Small" variant="Green">
                                        {{ $t('active-variants-badge-label') }}
                                    </Badge>
                                </template>
                                <template #completedBadge>
                                    <Badge size="Extra-Small" variant="Gray">
                                        {{ $t('completed-variants-badge-label') }}
                                    </Badge>
                                </template>

                                <template #mainVariants>
                                    <span class="font-semibold">
                                        {{ $t('main-variants') }}
                                    </span>
                                </template>

                                <template #mainVariantIndicator>
                                    <span class="relative inline-flex w-6">
                                        <span
                                            class="absolute -bottom-1.75 rounded inline-flex items-center justify-center text-white size-6 bg-rose-700"
                                        >
                                            <StarFilledIcon class="size-4" />
                                        </span>
                                    </span>
                                </template>

                                <template #toggleActionLabel>
                                    <span class="font-semibold">
                                        {{ $t('visible-toggle-label') }}
                                    </span>
                                </template>
                            </i18n>
                            <i18n
                                v-if="type === 'ExternalEvent'"
                                path="make-main-variants-visible-tooltip-external-event"
                                :args="{ allocationGroupType: props.type }"
                            >
                                <template #fixBadge>
                                    <Badge variant="Green" size="Extra-Small">
                                        {{ $t('fix-variants-badge-label') }}
                                    </Badge>
                                </template>
                                <template #optionalBadge>
                                    <Badge variant="Yellow" size="Extra-Small">
                                        {{ $t('optional-variants-badge-label') }}
                                    </Badge>
                                </template>
                                <template #completedBadge>
                                    <Badge variant="Gray" size="Extra-Small">
                                        {{ $t('completed-variants-badge-label') }}
                                    </Badge>
                                </template>

                                <template #mainVariants>
                                    <span class="font-semibold">
                                        {{ $t('main-variants') }}
                                    </span>
                                </template>

                                <template #mainVariantIndicator>
                                    <span class="relative inline-flex w-6">
                                        <span
                                            class="absolute -bottom-1.75 rounded inline-flex items-center justify-center text-white size-6 bg-green-700"
                                        >
                                            <StarFilledIcon class="size-4" />
                                        </span>
                                    </span>
                                </template>

                                <template #toggleActionLabel>
                                    <span class="font-semibold">
                                        {{ $t('visible-toggle-label') }}
                                    </span>
                                </template>
                            </i18n>
                        </template>
                    </Tooltip>

                    <Tooltip :is-teleported="true">
                        <template #trigger>
                            <Button
                                size="Extra-Small"
                                variant="Neutral-Light"
                                @click="
                                    triggerHideTooltip();
                                    emit('make-active-variants-visible');
                                "
                            >
                                <template #icon>
                                    <ToggleOnIcon class="size-4" />
                                </template>
                                {{ $t('make-active-variants-visible-label') }}
                            </Button>
                        </template>

                        <template #text>
                            <i18n
                                v-if="type === 'Event'"
                                path="make-active-variants-visible-tooltip-event"
                                tag="div"
                                :args="{ allocationGroupType: props.type }"
                            >
                                <template #activeBadge>
                                    <Badge size="Extra-Small" variant="Green">
                                        {{ $t('active-variants-badge-label') }}
                                    </Badge>
                                </template>
                                <template #passiveBadge>
                                    <Badge variant="Yellow" size="Extra-Small">
                                        {{ $t('passive-variants-badge-label') }}
                                    </Badge>
                                </template>
                                <template #completedBadge>
                                    <Badge size="Extra-Small" variant="Gray">
                                        {{ $t('completed-variants-badge-label') }}
                                    </Badge>
                                </template>
                                <template #canceledBadge>
                                    <Badge variant="Purple" size="Extra-Small">
                                        {{ $t('canceled-variants-badge-label') }}
                                    </Badge>
                                </template>

                                <template #activeVariants>
                                    <span class="font-semibold">
                                        {{ $t('active-variants') }}
                                    </span>
                                </template>

                                <template #activeVariantsBadges>
                                    <i18n path="active-variants-list">
                                        <template #active1>
                                            <Badge size="Extra-Small" variant="Green">
                                                {{ $t('active-1') }}
                                            </Badge>
                                        </template>

                                        <template #active2>
                                            <Badge size="Extra-Small" variant="Green">
                                                {{ $t('active-2') }}
                                            </Badge>
                                        </template>
                                    </i18n>
                                </template>

                                <template #toggleActionLabel>
                                    <span class="font-semibold">
                                        {{ $t('visible-toggle-label') }}
                                    </span>
                                </template>
                            </i18n>
                            <i18n
                                v-if="type === 'Project'"
                                path="make-active-variants-visible-tooltip-project"
                                tag="div"
                                :args="{ allocationGroupType: props.type }"
                            >
                                <template #activeBadge>
                                    <Badge size="Extra-Small" variant="Green">
                                        {{ $t('active-variants-badge-label') }}
                                    </Badge>
                                </template>
                                <template #completedBadge>
                                    <Badge size="Extra-Small" variant="Gray">
                                        {{ $t('completed-variants-badge-label') }}
                                    </Badge>
                                </template>

                                <template #activeVariants>
                                    <span class="font-semibold">
                                        {{ $t('active-variants') }}
                                    </span>
                                </template>

                                <template #activeVariantsBadges>
                                    <i18n path="active-variants-list">
                                        <template #active1>
                                            <Badge size="Extra-Small" variant="Green">
                                                {{ $t('active-1') }}
                                            </Badge>
                                        </template>

                                        <template #active2>
                                            <Badge size="Extra-Small" variant="Green">
                                                {{ $t('active-2') }}
                                            </Badge>
                                        </template>
                                    </i18n>
                                </template>

                                <template #toggleActionLabel>
                                    <span class="font-semibold">
                                        {{ $t('visible-toggle-label') }}
                                    </span>
                                </template>
                            </i18n>
                            <i18n
                                v-if="type === 'ExternalEvent'"
                                path="make-active-variants-visible-tooltip-external-event"
                                tag="div"
                                :args="{ allocationGroupType: props.type }"
                            >
                                <template #fixBadge>
                                    <Badge variant="Green" size="Extra-Small">
                                        {{ $t('fix-variants-badge-label') }}
                                    </Badge>
                                </template>
                                <template #optionalBadge>
                                    <Badge variant="Yellow" size="Extra-Small">
                                        {{ $t('optional-variants-badge-label') }}
                                    </Badge>
                                </template>
                                <template #completedBadge>
                                    <Badge variant="Gray" size="Extra-Small">
                                        {{ $t('completed-variants-badge-label') }}
                                    </Badge>
                                </template>

                                <template #activeVariants>
                                    <span class="font-semibold">
                                        {{ $t('active-variants') }}
                                    </span>
                                </template>

                                <template #activeVariantsBadges>
                                    <i18n path="active-variants-list">
                                        <template #active1>
                                            <Badge size="Extra-Small" variant="Green">
                                                {{ $t('active-1') }}
                                            </Badge>
                                        </template>

                                        <template #active2>
                                            <Badge size="Extra-Small" variant="Green">
                                                {{ $t('active-2') }}
                                            </Badge>
                                        </template>
                                    </i18n>
                                </template>

                                <template #toggleActionLabel>
                                    <span class="font-semibold">
                                        {{ $t('visible-toggle-label') }}
                                    </span>
                                </template>
                            </i18n>
                        </template>
                    </Tooltip>
                </div>
            </div>
        </div>

        <div class="space-y-8 pb-1">
            <slot />
        </div>
    </div>
</template>

<script setup lang="ts">
import { provide, ref } from 'vue';
import Badge from '../Badge/Badge.vue';
import Button from '../Button/Button.vue';
import CloseIcon from '../Icon/CloseIcon.vue';
import StarFilledIcon from '../Icon/StarFilledIcon.vue';
import ToggleOnIcon from '../Icon/ToggleOnIcon.vue';
import { triggerHideTooltipKey } from '../Tooltip/injectionKeys';
import Tooltip from '../Tooltip/Tooltip.vue';
import { AllocationGroupType } from './AllocationSelectionPanel.types';
import { allocationGroupKey } from './injectionKeys';

type AllocationSelectionPanelProps = {
    countAllocationVariants: number;
    type: AllocationGroupType;
};

type AllocationSelectionPanelEmits = {
    (e: 'close'): void;
    (e: 'make-main-variants-visible'): void;
    (e: 'make-active-variants-visible'): void;
};

const props = defineProps<AllocationSelectionPanelProps>();
const emit = defineEmits<AllocationSelectionPanelEmits>();

provide(allocationGroupKey, {
    type: props.type,
});

// This is used to force close the Tooltip after clicking the button
const triggerHideTooltipKeyCounter = ref(0);
provide(triggerHideTooltipKey, triggerHideTooltipKeyCounter);

function triggerHideTooltip() {
    triggerHideTooltipKeyCounter.value = triggerHideTooltipKeyCounter.value + 1;
}
</script>
